import React from 'react';

interface AddTransactionOptionsProps {
  accountName: string;
  company?: string; // broker/company name (e.g., 'etrade', 'schwab', 'fidelity', 'coinbase', 'vanguard')
  onManualClick: () => void;
  onUploadClick: () => void; // invoked when E*TRADE CSV
  onSchwabUploadClick?: () => void; // invoked when Schwab CSV
  onFidelityUploadClick?: () => void; // invoked when Fidelity CSV
  onCoinbaseUploadClick?: () => void; // invoked when Coinbase CSV
  onVanguardUploadClick?: () => void; // invoked when Vanguard CSV
  onDefaultCsvClick: () => void; // invoked for default CSV upload
}

const AddTransactionOptions: React.FC<AddTransactionOptionsProps> = ({
  accountName,
  company,
  onManualClick,
  onUploadClick,
  onSchwabUploadClick,
  onFidelityUploadClick,
  onCoinbaseUploadClick,
  onVanguardUploadClick,
  onDefaultCsvClick,
}) => {
  const brokerKey = (company || '').toLowerCase();
  const isEtrade = brokerKey === 'etrade';
  const isSchwab = brokerKey === 'schwab';
  const isFidelity = brokerKey === 'fidelity';
  const isCoinbase = brokerKey === 'coinbase';
  const isVanguard = brokerKey === 'vanguard';
  const displayNameMap: Record<string, string> = {
    etrade: 'E*Trade',
    schwab: 'Schwab',
    fidelity: 'Fidelity',
    coinbase: 'Coinbase',
    vanguard: 'Vanguard',
  };
  const displayName = displayNameMap[brokerKey] || (company || 'Broker');
  const uploadLabel = `Upload ${displayName} CSV`;
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Account "<span className="font-medium">{accountName}</span>" added. Now, add your transaction history.
      </p>
      
      <div className="grid grid-cols-1 gap-4">
        {/* E*TRADE CSV Upload Button (conditional) */}
        {isEtrade && (
          <button
            onClick={onUploadClick}
            className="flex flex-col items-center justify-center p-6 border rounded-lg text-center bg-white text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 space-y-2"
            title={uploadLabel}
          >
            <img
              src="/icon-upload-lrg.png"
              alt="Upload"
              className="h-8 w-8"
              style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
            />
            <span className="font-medium">{uploadLabel}</span>
          </button>
        )}

        {/* Schwab CSV Upload Button (conditional) */}
        {isSchwab && onSchwabUploadClick && (
          <button
            onClick={onSchwabUploadClick}
            className="flex flex-col items-center justify-center p-6 border rounded-lg text-center bg-white text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 space-y-2"
            title={uploadLabel}
          >
            <img
              src="/icon-upload-lrg.png"
              alt="Upload"
              className="h-8 w-8"
              style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
            />
            <span className="font-medium">{uploadLabel}</span>
          </button>
        )}

        {/* Fidelity CSV Upload Button (conditional) */}
        {isFidelity && onFidelityUploadClick && (
          <button
            onClick={onFidelityUploadClick}
            className="flex flex-col items-center justify-center p-6 border rounded-lg text-center bg-white text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 space-y-2"
            title={uploadLabel}
          >
            <img 
              src="/icon-upload-lrg.png" 
              alt="Upload" 
              className="h-8 w-8"
              style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
            />
            <span className="font-medium">{uploadLabel}</span>
          </button>
        )}

        {/* Coinbase CSV Upload Button (conditional) */}
        {isCoinbase && onCoinbaseUploadClick && (
          <button
            onClick={onCoinbaseUploadClick}
            className="flex flex-col items-center justify-center p-6 border rounded-lg text-center bg-white text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 space-y-2"
            title={uploadLabel}
          >
            <img 
              src="/icon-upload-lrg.png" 
              alt="Upload" 
              className="h-8 w-8"
              style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
            />
            <span className="font-medium">{uploadLabel}</span>
          </button>
        )}

        {/* Vanguard CSV Upload Button (conditional) */}
        {isVanguard && onVanguardUploadClick && (
          <button
            onClick={onVanguardUploadClick}
            className="flex flex-col items-center justify-center p-6 border rounded-lg text-center bg-white text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 space-y-2"
            title={uploadLabel}
          >
            <img
              src="/icon-upload-lrg.png"
              alt="Upload"
              className="h-8 w-8"
              style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
            />
            <span className="font-medium">{uploadLabel}</span>
          </button>
        )}

        {/* Upload Default CSV Button */}
        <button
          onClick={onDefaultCsvClick}
          className="flex flex-col items-center justify-center p-6 border rounded-lg text-center bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 space-y-2"
          title="Upload Default CSV"
        >
          <img 
            src="/icon-upload-lrg.png" 
            alt="Upload" 
            className="h-8 w-8"
            style={{ filter: 'brightness(0) saturate(100%) invert(30%) sepia(8%) saturate(1352%) hue-rotate(177deg) brightness(94%) contrast(86%)' }}
          />
          <span className="font-medium">Upload Default CSV</span>
        </button>

        {/* Manual Transaction Button */}
        <button
          onClick={onManualClick}
          className="flex flex-col items-center justify-center p-6 border border-gray-300 rounded-lg text-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 space-y-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <span className="font-medium text-gray-700">Manually add transactions</span>
        </button>
      </div>
    </div>
  );
};

export default AddTransactionOptions;
